import Grid from '@mui/material/Grid';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { copyPhasesForNewProject } from '~components/Order/PhaseDrawer/copyPhasesForNewProject';
import type { PhaseOptions } from '~components/Order/PhaseDrawer/types';
import { PhaseSelection } from '~components/Order/PhaseSelection';
import { AccountTypeahead } from '~hooks/useAccount/models';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import { BasicMaterial } from '~hooks/useMaterials/models';
import { Phase, Project } from '~hooks/useProjects/models';
import SuggestedRateDepartments from '~pages/Settings/DepartmentManagement/SuggestedRateDepartments';

interface ProjectDetailsV2Props {
  /**
   * Enables the pickup site→material→PO cascade — phase resolution
   * keyed on the (pickup site, material) pair with clear-on-transition.
   * Only the order form v3 ProjectDetailsPanel passes true; the legacy
   * NewOrderForm passes false and keeps the historical material-only
   * autofill untouched (the component is shared, so scope is a prop).
   */
  cascadeEnabled?: boolean;
  companyId?: string;
  isDepartmentRequired?: boolean;
  isRateByDepartmentNameRuleOn?: boolean;
  project?: Project;
  source?: 'order-form';
  tomlinsonOrderModalFeatureFlagEnabled?: boolean;
}

// The minimal slice of the order form's pickup-waypoint value the cascade
// reads (the full runtime shape is OrderWaypointFormValue in
// OrderFormV3Context.tsx — not imported here to keep this shared legacy
// component free of v3 module edges).
type PickUpWayPointFormValue = { site?: { id?: string } | null } | null;

// Content signature for a phase list, used to tell real edits apart from
// PhaseSelection's formatting-only echoes. PhaseSelection emits the list in
// two shapes — verbatim on hydration, then "filtered" (`_temp_…` ids
// rewritten to '', code-less rows dropped, never-persisted `_destroy` rows
// removed) — so the signature applies that same normalization to both sides:
// only rows that can reach the order payload count, and id spelling never
// does. Tolerates Phase model instances (the form's initial value) alongside
// plain option objects, which never compare equal by reference or JSON
// (TRE-14311).
function phaseSignature(phases: PhaseOptions[]) {
  return phases
    .map((phase) => ({
      id: phase.id?.startsWith('_temp_') ? '' : (phase.id ?? ''),
      name: phase.name ?? '',
      code: phase.code ?? '',
      primary: phase.primary ? 1 : 0,
      destroy: phase._destroy ?? 0,
    }))
    .filter((phase) => phase.code && !(phase.destroy && phase.id === ''))
    .map(
      (phase) =>
        `${phase.id}|${phase.name}|${phase.code}|${phase.primary}|${phase.destroy}`,
    )
    .join('&');
}

function ProjectDetailsV2({
  cascadeEnabled = false,
  companyId,
  isDepartmentRequired,
  isRateByDepartmentNameRuleOn = false,
  project,
  source,
  tomlinsonOrderModalFeatureFlagEnabled,
}: ProjectDetailsV2Props) {
  const form = useFormContext();

  const { getDepartmentByCompanyIdTypeahead } = useDepartment();

  const [loaded, setLoaded] = useState(false);
  const [localPhaseNameOptions, setLocalPhaseNameOptions] = useState<PhaseOptions[]>([]);
  const [selectedPhase, setSelectedPhase] = useState<PhaseOptions | null>(null);

  const watchedAccount = form.watch('account') as AccountTypeahead | null;
  const watchedProject = form.watch('project') as Project | null;
  const watchedPhases = form.watch('phases') as Phase[] | null;
  const watchedOrderId = form.watch('id') as string | undefined;

  const watchedMaterial = form.watch('material') as BasicMaterial | null;
  // The waypoint machinery writes the PARENT `pickUpWayPoint` path
  // (OrderFormV3Context.onPickUpSiteChange), and react-hook-form's render
  // watch only re-renders when the written path is the watched path or
  // deeper — so the cascade watches the parent object and derives the id.
  const watchedPickUpWayPoint = form.watch('pickUpWayPoint') as PickUpWayPointFormValue;
  const watchedPickUpSiteId = watchedPickUpWayPoint?.site?.id;

  const previousProjectIdRef = useRef<string | undefined>(project?.id);
  const previousMaterialIdRef = useRef<string | undefined>(
    (form.getValues('material') as BasicMaterial | null)?.id ?? undefined,
  );
  // Initialized to the CURRENT pickup site so the cascade resolver never fires
  // on mount — this protects a saved order's PO on edit and a copied order's PO
  // on Copy & Edit (both mount with an id in form state).
  const previousPickUpSiteIdRef = useRef<string | undefined>(
    (form.getValues('pickUpWayPoint') as PickUpWayPointFormValue)?.site?.id,
  );
  // A genuine pickup-site or material change can land while the project's
  // material totals are still fetching. The refs above advance regardless, so
  // that re-selecting the same value still registers as a change — which means
  // the pending trigger has to be remembered here and replayed once the totals
  // arrive, or the auto-fill is silently skipped.
  const deferredCascadeTriggerRef = useRef<{
    material: boolean;
    pickUpSite: boolean;
    previousMaterialId: string | undefined;
  } | null>(null);
  const isInitialLoadRef = useRef(true);
  const userChangedProjectRef = useRef(false);

  const handlePhaseNameChange = useCallback((name: string | null) => {
    form.setValue('phaseName', name || null, { shouldDirty: false });
  }, []);

  const handlePhaseCodeChange = useCallback((code: string | null) => {
    form.setValue('phaseCode', code || null, { shouldDirty: false });
  }, []);

  // The shared `phase` + `phases[].primary` writer behind both the user pick
  // path (handlePhaseChange) and the cascade path (applyCascadePhaseSelection).
  // The primary markers must always move together with `phase`: the project's
  // carried-forward primary leaves `phases[].primary` set and the backend
  // early-returns when any primary exists, so a half-cleared state would
  // submit the OLD project-primary PO.
  const writePhaseToForm = useCallback(
    (selection: PhaseOptions | null, options: { shouldDirty: boolean }) => {
      const { shouldDirty } = options;
      form.setValue(
        'phase',
        selection
          ? { id: selection.id ?? '', name: selection.name, code: selection.code ?? '' }
          : { id: '', name: '', code: '' },
        { shouldDirty },
      );
      const currentPhases = (form.getValues('phases') ?? []) as PhaseOptions[];
      form.setValue(
        'phases',
        currentPhases.map((p) => ({
          ...p,
          primary: Boolean(
            selection && p.name === selection.name && p.code === selection.code,
          ),
        })),
        { shouldDirty },
      );
    },
    [form],
  );

  // PhaseSelection has its own internal useForm; the user's pick reaches the
  // parent form only through this callback. Without it, `phase` stays at its
  // default empty value and `phases[]` carries no `primary: true` marker.
  // PhaseSelection only invokes onChange for user actions, so flagging dirty
  // here is safe — the Submit button is gated on form dirtiness (TRE-14311).
  const handlePhaseChange = useCallback(
    (selection: PhaseOptions | null) => {
      // Under the cascade the selectedPhase prop must track the user's pick:
      // a later cascade clear has to transition the PhaseSelection prop, and
      // a null→null no-op would leave the visible pick while the payload is
      // cleared. Legacy keeps its historical prop behavior untouched.
      if (cascadeEnabled) {
        setSelectedPhase(selection);
      }
      writePhaseToForm(selection, { shouldDirty: true });
    },
    [cascadeEnabled, writePhaseToForm],
  );

  // The ONE atomic writer for every cascade-driven phase write — selectedPhase
  // (local state), `phase`, `phaseName`, `phaseCode`, and every
  // `phases[].primary` marker. Cascade writes are not user edits, so nothing
  // here flags the form dirty.
  const applyCascadePhaseSelection = useCallback(
    (selection: PhaseOptions | null) => {
      setSelectedPhase(selection);
      form.setValue('phaseName', selection?.name || null, { shouldDirty: false });
      form.setValue('phaseCode', selection?.code || null, { shouldDirty: false });
      writePhaseToForm(selection, { shouldDirty: false });
    },
    [form, writePhaseToForm],
  );

  // PhaseSelection re-emits its option list on hydration as well as after
  // drawer edits (add / edit / delete rows). Only real content changes may
  // mark the form dirty — Submit is gated on dirtiness, and hydration echoes
  // must not enable it on an untouched form (TRE-14311).
  const handlePhaseOptionsChange = useCallback(
    (options: PhaseOptions[]) => {
      const currentPhases = (form.getValues('phases') ?? []) as PhaseOptions[];
      const phasesChanged = phaseSignature(currentPhases) !== phaseSignature(options);
      form.setValue('phases', options, { shouldDirty: phasesChanged });
    },
    [form],
  );

  // Track when user changes the project in the form
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    // If we're not in initial load and the watched project changed, mark as user change
    if (watchedProject?.id !== previousProjectIdRef.current) {
      userChangedProjectRef.current = true;
    }
  }, [watchedProject?.id]);

  useEffect(() => {
    if (watchedAccount?.id && !watchedProject?.id) {
      setSelectedPhase(null);
    }
  }, [watchedAccount?.id, watchedProject?.id]);

  useEffect(() => {
    // only clear phase options on create, not on update
    if (!watchedOrderId && !watchedProject?.id) {
      setLocalPhaseNameOptions([]);
      setSelectedPhase(null);
    }
  }, [watchedProject?.id, watchedOrderId]);

  useEffect(() => {
    if (!loaded) {
      if (watchedPhases) {
        setLocalPhaseNameOptions(
          watchedPhases.map((item: Phase) => ({
            id: item.id,
            name: item.name,
            code: item.code,
            primary: item.primary,
          })),
        );

        const primary = watchedPhases.find((item: Phase) => item.primary);

        if (primary) {
          const phaseOption: PhaseOptions = {
            id: primary.id,
            name: primary.name,
            code: primary.code,
            primary: true,
            _destroy: 0,
          };

          setSelectedPhase(phaseOption);
        }
      }

      setLoaded(true);
    }
  }, [JSON.stringify(watchedPhases), loaded]);

  useEffect(() => {
    const currentProjectId = project?.id || '';
    const previousProjectId = previousProjectIdRef.current || '';

    // Only trigger if loaded, there's a change, AND the user actually changed the project
    if (
      loaded &&
      previousProjectId !== currentProjectId &&
      userChangedProjectRef.current
    ) {
      if (project?.id) {
        // Mark existing phases as deleted
        const existingPhasesMarkedAsDeleted = localPhaseNameOptions.map((phase) => ({
          ...phase,
          _destroy: 1,
        }));

        const newPhasesWithoutIds = copyPhasesForNewProject(project.phases);

        // Combine deleted existing phases with new phases
        const combinedPhases = [...existingPhasesMarkedAsDeleted, ...newPhasesWithoutIds];

        setLocalPhaseNameOptions(combinedPhases);
        // Also update the form's phases value
        form.setValue('phases', combinedPhases, { shouldDirty: false });

        const primary = newPhasesWithoutIds.find((item) => item.primary);
        if (primary) {
          setSelectedPhase(primary as PhaseOptions);
        } else {
          setSelectedPhase(null);
        }
      } else {
        // Project was cleared - mark all existing phases as deleted
        const allPhasesMarkedAsDeleted = localPhaseNameOptions.map((phase) => ({
          ...phase,
          _destroy: 1,
        }));

        setLocalPhaseNameOptions(allPhasesMarkedAsDeleted);
        // Also update the form's phases value
        form.setValue('phases', allPhasesMarkedAsDeleted, { shouldDirty: false });
        setSelectedPhase(null);
      }

      // Reset the user change flag after processing
      userChangedProjectRef.current = false;
    }

    // Always update the ref to track the current project ID
    previousProjectIdRef.current = project?.id;
  }, [project?.id, project?.phases, loaded, JSON.stringify(localPhaseNameOptions)]);

  // Auto-fill phase when material changes based on project material-phase link.
  // With cascadeEnabled (order form v3) resolution keys on the
  // (pickup site, material) pair against the project's material totals,
  // mirroring the backend candidate rule: candidates are the material's rows
  // at the pickup site plus its null-site rows; exact-site rows win; the phase
  // auto-fills only when exactly one candidate with a phase remains — 0 or >1
  // leaves it cleared for a manual PhaseSelection pick. Ref-initialized change
  // detection keeps the resolver silent on mount in every mode.
  useEffect(() => {
    const materialId = watchedMaterial?.id;

    const materialIdChanged = materialId !== previousMaterialIdRef.current;
    const pickUpSiteIdChanged =
      cascadeEnabled && watchedPickUpSiteId !== previousPickUpSiteIdRef.current;
    const deferredTrigger = deferredCascadeTriggerRef.current;
    if (!materialIdChanged && !pickUpSiteIdChanged && !deferredTrigger) return;

    const seenPreviousMaterialId = previousMaterialIdRef.current;
    previousMaterialIdRef.current = materialId;
    previousPickUpSiteIdRef.current = watchedPickUpSiteId;

    const materialChanged = materialIdChanged || Boolean(deferredTrigger?.material);
    const pickUpSiteChanged = pickUpSiteIdChanged || Boolean(deferredTrigger?.pickUpSite);
    // The transition-away rule needs the material we were on before the change;
    // on a replay the seen-ref has already moved past it.
    const previousMaterialId =
      deferredTrigger?.previousMaterialId ?? seenPreviousMaterialId;

    if (!loaded || !project?.projectMaterialTotals?.length) {
      deferredCascadeTriggerRef.current = {
        material: materialChanged,
        pickUpSite: pickUpSiteChanged,
        previousMaterialId,
      };
      return;
    }
    deferredCascadeTriggerRef.current = null;
    const projectMaterialTotals = project.projectMaterialTotals;

    const legacyMaterialOnlyAutofill = () => {
      if (!materialId) return;
      const pmt = projectMaterialTotals.find((item) => item.material?.id === materialId);
      if (!pmt?.phase) return;

      const matchingPhase = localPhaseNameOptions.find(
        (p) => !p._destroy && p.name === pmt.phase?.name && p.code === pmt.phase?.code,
      );
      if (matchingPhase) {
        setSelectedPhase(matchingPhase);
      }
    };

    if (!cascadeEnabled) {
      legacyMaterialOnlyAutofill();
      return;
    }

    const totalsForMaterial = (id: string | undefined) =>
      id ? projectMaterialTotals.filter((item) => item.material?.id === id) : [];
    const materialTotals = totalsForMaterial(materialId);
    const materialHasSiteTotals = materialTotals.some((item) => !isNil(item.site));
    const previousMaterialHadSiteTotals =
      materialChanged &&
      totalsForMaterial(previousMaterialId).some((item) => !isNil(item.site));

    // Site-aware resolution engages only when a site-bearing row is involved
    // (on either side of a material change). A material with no site-bearing
    // rows keeps the legacy material-only autofill byte-identical (the BE
    // tri-state "legacy" branch), and a pickup-only change never touches it.
    const cascadeApplies = materialHasSiteTotals || previousMaterialHadSiteTotals;
    if (!cascadeApplies) {
      if (materialChanged) {
        legacyMaterialOnlyAutofill();
      }
      return;
    }

    // Clear-on-transition: atomically wipe selectedPhase/phase/phaseName/
    // phaseCode and every phases[].primary marker BEFORE re-resolving.
    applyCascadePhaseSelection(null);

    const exactSiteCandidates = watchedPickUpSiteId
      ? materialTotals.filter((item) => item.site?.id === watchedPickUpSiteId)
      : [];
    const nullSiteCandidates = materialTotals.filter((item) => isNil(item.site));
    const candidates =
      exactSiteCandidates.length > 0 ? exactSiteCandidates : nullSiteCandidates;
    if (candidates.length !== 1) return;

    const resolvedPhase = candidates[0].phase;
    if (!resolvedPhase) return;

    const matchingPhase = localPhaseNameOptions.find(
      (p) =>
        !p._destroy && p.name === resolvedPhase.name && p.code === resolvedPhase.code,
    );
    if (matchingPhase) {
      applyCascadePhaseSelection(matchingPhase);
    }
  }, [
    watchedMaterial?.id,
    watchedPickUpSiteId,
    cascadeEnabled,
    loaded,
    project,
    localPhaseNameOptions,
    applyCascadePhaseSelection,
  ]);

  return (
    <>
      <Grid size={{ xs: 6 }}>
        {!tomlinsonOrderModalFeatureFlagEnabled ? (
          <TextFormField
            control={form.control}
            disabled
            errors={form.formState.errors}
            isRequired={false}
            label={`${t('form_fields.project_id')}`}
            name="projectExternalId"
            dataUi={`page-sales-orders__project-details-v2__project-external-id-input`}
          />
        ) : null}
      </Grid>
      <Grid size={{ xs: 6 }}>
        <AutocompleteAsyncFormField
          key={companyId ?? 'no-company'}
          clearable
          control={form.control}
          disabled={!companyId}
          errors={form.formState.errors}
          asyncCallback={getDepartmentByCompanyIdTypeahead}
          extraRequestOptions={{ companyId }}
          getLabel={(item) => item.name || ''}
          getValue={(item) => item.id}
          isRequired={isDepartmentRequired}
          label={`${t('project.form.department')}`}
          name="department"
          sx={{ mr: 2 }}
          dataUi={`page-sales-orders__project-details-v2__department-input`}
        />
        <SuggestedRateDepartments
          companyId={companyId}
          isRateByDepartmentNameRuleOn={isRateByDepartmentNameRuleOn}
          onCreated={(department) => {
            form.setValue('department', department, { shouldDirty: true });
            void form.trigger('department');
          }}
          sx={{ mt: 1 }}
        />
      </Grid>
      {/* We need to wait for phases to load into localPhaseNameOptions beforehand so that onPhaseOptionsChange does not set phases to empty too early */}
      {/* TRE-14311: phases added or edited here only travel with the order payload
          (they are never written back to the selected project), so the drawer stays
          editable even when creating an order for a project that already has phases */}
      {loaded && (
        <PhaseSelection
          canEdit
          onChange={handlePhaseChange}
          onPhaseOptionsChange={handlePhaseOptionsChange}
          phaseOptions={localPhaseNameOptions}
          selectedPhase={selectedPhase}
          alignListboxRight
          isFormSubmitted={
            source === 'order-form' ? form.control._formState.isSubmitted : undefined
          }
          onPhaseNameChange={handlePhaseNameChange}
          onPhaseCodeChange={handlePhaseCodeChange}
        />
      )}
    </>
  );
}

export default observer(ProjectDetailsV2);
